import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import './Statistics.css';
import { API_BASE } from '../config';
import { tr } from '../i18n';

// The sales namespace is mounted at /sales_reports on the cloud server.
const NS = `${API_BASE}/sales_reports/statistics`;

// Two token shapes exist in this app: pages that take the `token` prop read
// `token.access_token`, while others re-read sessionStorage and reach for
// `.data.access_token`. Accept both rather than assuming one.
const authHeader = (token) => {
  if (token && token.access_token) return token.access_token;
  try {
    return JSON.parse(sessionStorage.getItem('token')).data.access_token;
  } catch (e) {
    return '';
  }
};

const PERIODS = [
  { key: 'current_day', label: tr("Today"), method: 'POST', path: '/current_day' },
  { key: 'previous_day', label: tr("Yesterday"), method: 'GET', path: '/daily' },
  { key: 'current_month', label: tr("This Month"), method: 'POST', path: '/current_month' },
  { key: 'previous_month', label: tr("Last Month"), method: 'GET', path: '/monthly' },
];

// Product identity, matching the fuel colours used on the ATG page.
const PRODUCTS = [
  { key: 'unleaded', label: tr("Unleaded"), icon: 'fa-gas-pump', tint: 'is-unleaded' },
  { key: 'diesel', label: tr("Diesel"), icon: 'fa-truck-moving', tint: 'is-diesel' },
];

const nf0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtVolume = (v) => (v == null ? '—' : nf2.format(v));
const fmtMoney = (v) => (v == null ? '—' : nf0.format(v));
const fmtCount = (v) => (v == null ? '—' : nf0.format(v));

const Statistics = ({ token, LogoutTimeout }) => {
  const [period, setPeriod] = useState('current_day');
  // One entry per period key. Once a tab has been fetched its figures stay put
  // until the refresh button is pressed — switching tabs never re-queries, and
  // nothing reloads on a timer. These endpoints scan the sales table, so a
  // background poll is expensive and the numbers are not live enough to
  // justify it.
  const [cache, setCache] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const data = cache[period] || null;
  const error = errors[period] || '';

  // Depend on the token STRING, not the token object. App.js re-renders on its
  // own timers and hands down a fresh object each time; keying the callback on
  // the object identity made `load` new on every render, which re-fired the
  // effect below and put the page in a permanent fetch loop.
  const authToken = authHeader(token);

  // Cancels the previous request when the tab changes, so a slow response
  // cannot land after a newer one and overwrite it.
  const abortRef = useRef(null);

  // Mirror of `cache` so the fetch callback can check it without listing it as
  // a dependency — doing that would rebuild the callback on every fetch and
  // start the effect loop again.
  const cacheRef = useRef({});
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  const load = useCallback(async (force) => {
    const cfg = PERIODS.find((p) => p.key === period);

    // Already fetched and not an explicit refresh: show what we have.
    if (!force && cacheRef.current[period]) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setErrors((prev) => ({ ...prev, [period]: '' }));

    try {
      const options = {
        method: cfg.method,
        headers: { Authorization: authToken },
        signal: controller.signal,
      };
      // Only the POST endpoints take a body. Sending Content-Type on the GETs
      // is harmless but sending a body is not, so keep them clean.
      if (cfg.method === 'POST') {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({});
      }

      const res = await fetch(NS + cfg.path, options);
      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      const json = await res.json();
      setCache((prev) => ({ ...prev, [period]: json }));
    } catch (e) {
      // An abort is this component cancelling its own request, not a failure —
      // showing an error dialog for it would be wrong.
      if (e.name === 'AbortError') return;
      setErrors((prev) => ({
        ...prev,
        [period]: e.message || tr('Could not load statistics.'),
      }));
      Swal.fire({
        icon: 'error',
        title: tr("Could not load statistics"),
        text: e.message,
      });
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, [period, authToken]);

  // Fetch on mount and whenever the period changes. LogoutTimeout is
  // deliberately NOT called here: it sets state in App.js, App re-renders, this
  // effect fires again, and the page loops. It belongs on user actions instead.
  // Fetches a tab the first time it is opened, and never again unless the
  // refresh button is pressed. There is no interval and no re-fetch on
  // re-render.
  useEffect(() => {
    load(false);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  const totalVolume =
    data && (data.total_sale_unleaded || 0) + (data.total_sale_diesel || 0);
  const totalAmount =
    data && (data.total_amount_unleaded || 0) + (data.total_amount_diesel || 0);
  const totalReceipts =
    data &&
    (data.total_receipts_unleaded || 0) + (data.total_receipts_diesel || 0);

  const activeLabel = (PERIODS.find((p) => p.key === period) || {}).label;

  return (
    <div className="statistics-page">
      {/* ── Header ───────────────────────────────────────── */}
      <div className="stats-header">
        <div className="stats-heading">
          <h1>
            <i className="fas fa-chart-line" /> {tr("Statistics")}
          </h1>
          <p>
            {tr("Volume, revenue and receipts by product, with the leading station and region for the selected period.")}
          </p>
        </div>

        <div className="stats-controls">
          <div className="stats-tabs" role="tablist">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={period === p.key}
                className={`stats-tab${period === p.key ? ' is-active' : ''}`}
                onClick={() => {
                  if (LogoutTimeout) LogoutTimeout();
                  setPeriod(p.key);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="stats-refresh"
            onClick={() => {
              if (LogoutTimeout) LogoutTimeout();
              load(true);
            }}
            disabled={loading}
            title={tr("Refresh")}
          >
            <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Period meta ──────────────────────────────────── */}
      {data && !loading && (
        <div className="stats-meta">
          <span className="stats-window">
            {data.start} &rarr; {data.end}
          </span>
          {/* "live" means computed on request; "stored" means read from the
              nightly job. Worth surfacing — a stored figure can lag a late
              station sync, and the timestamp says how stale it might be. */}
          <span className={`stats-source is-${data.source}`}>
            {data.source === 'stored' ? '' : tr("Live")}
          </span>
          
        </div>
      )}

      {loading && (
        <div className="stats-loading">
          <i className="fas fa-circle-notch fa-spin" />
          <span>{tr("Loading")} {activeLabel && activeLabel.toLowerCase()}…</span>
        </div>
      )}

      {!loading && error && (
        <div className="stats-empty">
          <i className="fas fa-exclamation-triangle" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* ── Combined totals ──────────────────────────── */}
          <div className="stats-summary">
            <div className="stats-kpi">
              <span className="stats-kpi-label">{tr("Total Volume")}</span>
              <span className="stats-kpi-value">
                {fmtVolume(totalVolume)} <small>{tr("L")}</small>
              </span>
            </div>
            <div className="stats-kpi">
              <span className="stats-kpi-label">{tr("Total Amount")}</span>
              <span className="stats-kpi-value">
                {fmtMoney(totalAmount)} <small>{tr("TSH")}</small>
              </span>
            </div>
            <div className="stats-kpi">
              <span className="stats-kpi-label">{tr("Transactions")}</span>
              <span className="stats-kpi-value">{fmtCount(totalReceipts)}</span>
            </div>
          </div>

          {/* ── Per product ──────────────────────────────── */}
          <div className="stats-grid">
            {PRODUCTS.map((p) => {
              const volume = data[`total_sale_${p.key}`];
              const amount = data[`total_amount_${p.key}`];
              const receipts = data[`total_receipts_${p.key}`];
              const station = data[`top_station_${p.key}`];
              const region = data[`top_region_${p.key}`];

              return (
                <section key={p.key} className={`stats-card ${p.tint}`}>
                  <header className="stats-card-head">
                    <i className={`fas ${p.icon}`} />
                    <h2>{p.label}</h2>
                  </header>

                  <div className="stats-figures">
                    <div className="stats-figure">
                      <span className="stats-figure-label">{tr("Volume")}</span>
                      <span className="stats-figure-value">
                        {fmtVolume(volume)} <small>{tr("L")}</small>
                      </span>
                    </div>
                    <div className="stats-figure">
                      <span className="stats-figure-label">{tr("Amount")}</span>
                      <span className="stats-figure-value">
                        {fmtMoney(amount)} <small>{tr("TSH")}</small>
                      </span>
                    </div>
                    <div className="stats-figure">
                      <span className="stats-figure-label">{tr("Transactions")}</span>
                      <span className="stats-figure-value">
                        {fmtCount(receipts)}
                      </span>
                    </div>
                  </div>

                  <div className="stats-tops">
                    <div className="stats-top">
                      <span className="stats-top-label">
                        <i className="fas fa-trophy" /> {tr("Top station")}
                      </span>
                      {station ? (
                        <>
                          <span className="stats-top-name">
                            {station.station_name || station.station_serial}
                          </span>
                          <span className="stats-top-sub">
                            {station.region || '—'}
                            {station.district ? ` · ${station.district}` : ''}
                          </span>
                          <span className="stats-top-figure">
                            {tr("{0} L ·", fmtVolume(station.total_volume))}{' '}
                            {tr("{0} TSH", fmtMoney(station.total_amount))}
                          </span>
                        </>
                      ) : (
                        <span className="stats-top-none">{tr("No sales")}</span>
                      )}
                    </div>

                    <div className="stats-top">
                      <span className="stats-top-label">
                        <i className="fas fa-map-marker-alt" /> {tr("Top region")}
                      </span>
                      {region ? (
                        <>
                          <span className="stats-top-name">{region.region}</span>
                          <span className="stats-top-figure">
                            {tr("{0} L ·", fmtVolume(region.total_volume))}{' '}
                            {tr("{0} TSH", fmtMoney(region.total_amount))}
                          </span>
                        </>
                      ) : (
                        <span className="stats-top-none">
                          {tr("No region data")}
                        </span>
                      )}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Statistics;